/**
 * Fallback message resolution for $filters.responseMessage (bsapi#107).
 *
 * responseMessage matches only `alert_message.response_message` -- the shape
 * Helper::error_handler and Helper::success_handler emit. Every other envelope
 * fell through and showed NOTHING: not a generic error, silence. That covers
 * bsapi's bootstrap/app.php render handlers ({message, error_code} for 401,
 * validation 422, every HttpException and the production 500), StorageController's
 * bespoke {success:false, error:...} refusals, and hand-built call-site payloads
 * such as { data: { message: '...' } }.
 *
 * Returns null when nothing readable is present, so an unrecognised value stays
 * silent rather than toasting "undefined".
 */
export function fallbackMessage(val: any): { message: string; isError: boolean } | null {
    if (!val || typeof val !== 'object') return null;

    // axios.ts rejects with an Error carrying .data/.status; some call sites pass
    // the raw axios error instead, whose body sits under .response.data.
    const bodies: any[] = [val, val.data, val.response, val.response?.data].filter(
        (body) => body && typeof body === 'object'
    );

    let message = '';
    for (const body of bodies) {
        const candidate =
            (typeof body.alert_message === 'string' ? body.alert_message : body.alert_message?.response_message) ||
            body.message ||
            (typeof body.error === 'string' ? body.error : '');
        if (typeof candidate === 'string' && candidate.trim() !== '') {
            message = candidate.trim();
            break;
        }
    }
    if (!message) return null;

    let status: number | null = null;
    for (const body of bodies) {
        const raw = body.status;
        if (typeof raw === 'number' && !isNaN(raw)) {
            status = raw;
            break;
        }
        if (typeof raw === 'string' && raw.trim() !== '' && !isNaN(Number(raw))) {
            status = Number(raw);
            break;
        }
    }

    // A failed request always reaches here with a status -- axios.ts stamps the
    // HTTP status onto the Error it rejects with. A payload carrying no status
    // and no error marker is therefore a hand-built notice, not a failure, and
    // reddening it would be a worse regression than the silence being fixed.
    const hasErrorSignal =
        val instanceof Error ||
        bodies.some((body) => body.error_code || body.errors || body.success === false || typeof body.error === 'string');

    return { message, isError: status !== null ? status >= 400 : hasErrorSignal };
}
